import {ChronologicalCommentsSort} from '@github-ui/commenting/ChronologicalCommentsSort'
import {useCommentEditsContext} from '@github-ui/commenting/CommentEditsContext'
import type {IssueCommentComposerFragment} from '@github-ui/commenting/IssueCommentComposer'
import type {MarkdownComposerRef} from '@github-ui/commenting/useMarkdownBody'
import {VALUES as COMMENTING_VALUES} from '@github-ui/commenting/Values'
import {CopilotSurvey} from '@github-ui/copilot-survey-card'
import {dynamic} from '@github-ui/dynamic'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {IssueAgentTasksProvider} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {IssueBody, type IssueBodyFragment} from '@github-ui/issue-body/IssueBody'
import {useItemPickersContext} from '@github-ui/item-picker-base/ItemPickersContext'
import {
  type IssueViewerFeed,
  issueViewerFeedFromTimelineItemTypes,
} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {lazyWithPreload} from '@github-ui/react-core/lazy-with-preload'
import {useBeforeUnload} from '@github-ui/react-router'
import {handleRepositoryDataError, reloadPage} from '@github-ui/relay-error-helpers'
import PreloadedQueryBoundary from '@github-ui/relay-preloaded-query-boundary'
import {DEFAULT_RELAY_CACHE_TTL} from '@github-ui/relay-query-cache/helpers'
import {useCacheQueryLoader} from '@github-ui/relay-query-cache/hooks/use-cache-query-loader'
import {useScribeArtifacts, useScribeArtifactsApiEnabled} from '@github-ui/scribe-artifacts'
import {ArtifactsPanelProvider} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {SubIssuesListItemNestedSubIssuesServerQueryQuery} from '@github-ui/server-persisted-queries/types/SubIssuesListItemNestedSubIssuesServerQuery'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {StickySidebar} from '@github-ui/sticky-sidebar'
import {nestedSubIssuesQueryKey} from '@github-ui/sub-issues/fetchNestedSubIssues'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import {SubIssuesDataContext, useSubIssuesData} from '@github-ui/sub-issues/SubIssuesDataContext'
import {SubIssuesPresenceContext} from '@github-ui/sub-issues/SubIssuesPresenceContext'
import {useSubIssueState} from '@github-ui/sub-issues/SubIssueStateContext'
import {useCanEditSubIssues} from '@github-ui/sub-issues/useCanEditSubIssues'
import {useHasSubIssues} from '@github-ui/sub-issues/useHasSubIssues'
import {ISSUE_EVENTS} from '@github-ui/timeline-items/Events'
import {getHighlightedEventText} from '@github-ui/timeline-items/HighlightedEvent'
import {LABELS} from '@github-ui/timeline-items/Labels'
import {TimelineConnectorContext} from '@github-ui/timeline-items/TimelineDivider'
import {useStickyObserver} from '@github-ui/use-sticky-header/useStickyObserver'
import {Banner, Heading} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {graphql, type PreloadedQuery, usePreloadedQuery} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {CLASS_NAMES} from '../constants/class-names'
import {IDS} from '../constants/ids'
import {LABELS as VIEWER_LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import {useInputElementActiveContext} from '../contexts/InputElementActiveContext'
import {useHash} from '../hooks/use-hash'
import {useSidePanelScrollReset} from '../hooks/use-side-panel-scroll-reset'
import type {ItemIdentifier} from '../types/issue'
import {canonicalIssueUrl} from '../utils/urls'
import type {IssueViewerIssue$data, IssueViewerIssue$key} from './__generated__/IssueViewerIssue.graphql'
import type {IssueViewerViewQuery, IssueViewerViewQuery$variables} from './__generated__/IssueViewerViewQuery.graphql'
import {ArtifactsPanelLayout} from './artifacts-section/ArtifactsPanelLayout'
import {ArtifactsSection} from './artifacts-section/ArtifactsSection'
import {CreateArtifactMenuButton} from './artifacts-section/CreateArtifactMenuButton'
import {
  shouldFetchIssueArtifacts,
  shouldShowArtifactsSection,
  shouldShowCreateArtifactButton,
} from './artifacts-section/issue-artifacts-visibility'
import {IssueArtifactsAliveUpdates} from './artifacts-section/IssueArtifactsAliveUpdates'
import {LinkedArtifact} from './artifacts-section/LinkedArtifact'
import {ContentWrapper} from './ContentWrapper'
import {ConvertedToDiscussionBanner} from './ConvertedToDiscussionBanner'
import {EmuContributionBlockedBanner} from './EmuContributionBlockedBanner'
import {issueViewerSecondaryViewQueryKey} from './fetch-issue-viewer-secondary-view'
import {STICKY_HEADER_SIDEBAR_OFFSET} from './header/constants/sticky-elements'
import {Header, type HeaderFragment} from './header/Header'
import {IssueHeaderDataContext, useIssueHeaderRefetch} from './header/IssueHeaderDataContext'
import {StickyHeader} from './header/StickyHeader'
import {useIssueMetadata} from './issue-metadata-query'
import type {IssueNode, IssueViewerPageViewerNode} from './issue-viewer-page-query-types'
import type {IssueSecondaryNode} from './issue-viewer-secondary-query-types'
import {buildIssueViewerViewQueryVariables} from './issue-viewer-view-query-variables'
import {IssueSidebar, type IssueSidebarPrimaryFragment} from './IssueSidebar'
import {IssueTimelineErrorFallback} from './IssueTimelineErrorFallback'
import {IssueTimelineLoading} from './IssueTimelineLoading'
import styles from './IssueViewer.module.css'
import {IssueViewerLoading} from './IssueViewerLoading'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import {OptimisticUpdateRetry} from './OptimisticUpdateRetry'
import {ISSUE_VIEWER_DEFAULT_CONFIG, type IssueDeletionConnection, type OptionConfig} from './OptionConfig'
import {RecentAgentActivity} from './recent-agent-activity/RecentAgentActivity'
import type {RecentAgentActivityFragment} from './recent-agent-activity/use-recent-agent-activity'
import {NotFoundError, renderIssueViewerErrors} from './shared/IssueViewerError'
import {SidePanelMetadataDrawer} from './SidePanelMetadataDrawer'
import {SignedOutBanner} from './SignedOutBanner'
import {
  ActivityFilterEmptyState,
  ActivityFilterMenu,
  ActivityFiltersSetAsideNotice,
  ActivityLoadingState,
  ActivitySortMenu,
} from './timeline/ActivityTimelineControls'
import {CommentsHeader} from './timeline/CommentsHeader'
import type {TimelineFeed} from './timeline/fetch-timeline-items'
import {IssueTimeline, type IssueTimelineRenderProps} from './timeline/IssueTimeline'
import {ActivityEmptyState, TimelineActivityMenu} from './timeline/TimelineActivityMenu'
import {buildTimelineConnectionIds, TimelineConnectionProvider} from './timeline/TimelineConnectionContext'
import {useActivityTimelineView} from './timeline/use-activity-timeline-view'
import {useIssueCommentsSort} from './timeline/use-issue-comments-sort'
import {useIssueViewerContainerQueryFix} from './use-issue-viewer-container-query-fix'

const ISSUE_ARTIFACTS_PER_PAGE = 30

function IssueCommentComposerErrorFallback() {
  return (
    <Banner variant="critical" role="alert" title="Comment form unavailable">
      The comment form cannot be displayed. Reload the page to try again.
    </Banner>
  )
}

export type IssueViewerQueries = {
  issueViewerViewQuery: IssueViewerViewQuery
}

// RELAY-CLEANUP(IssueViewerViewQuery): the parent query is still read via `usePreloadedQuery` in
// `IssueViewerInternal`. Remove the RelayCompat widening when that component leaves Relay.
export type IssueViewerViewerFragment = RelayCompat<
  Pick<
    IssueViewerPageViewerNode,
    | 'isEnterpriseManagedUser'
    | 'enterpriseManagedEnterpriseId'
    | 'login'
    | '__typename'
    | 'id'
    | 'name'
    | 'profileResourcePath'
    | 'avatarUrl'
  >
>

type IssueViewerProps = {
  itemIdentifier: ItemIdentifier
  optionConfig?: OptionConfig
  linkedArtifactNumber?: number
}

type IssueViewerSecondaryDataProps = {
  secondaryIssueData?: IssueSecondaryNode
  initialNestedSubIssuesData?: SubIssuesListItemNestedSubIssuesServerQueryQuery
  initialNestedSubIssuesDataUpdatedAt?: number
  hideClosedSubIssuesEnabled?: boolean
  subIssueDeletionConnection?: IssueDeletionConnection
  timelinePage?: number
  issueUpdatesChannel?: string | null
  linkedArtifactNumber?: number
}

type IssueViewerInternalProps = {
  optionConfig: OptionConfig
  issueViewerViewRef: PreloadedQuery<IssueViewerViewQuery>
  owner: string
  repo: string
  number: number
  retryQuery?: () => void
} & IssueViewerSecondaryDataProps

type IssueViewerCoreBaseProps = {
  optionConfig: OptionConfig
  viewer: IssueViewerViewerFragment | null
  feed: IssueViewerFeed
  /** Cursor the newest timeline window pins to, derived from the page payload by `timeline-prefill`. */
  initialBackAnchor?: string
  routeQueryKey?: readonly unknown[]
  isRepoOwnerEnterpriseManaged?: boolean | null
} & IssueViewerSecondaryDataProps

type IssueViewerInternalFragmentProps = IssueViewerCoreBaseProps & {
  issueFragment: IssueViewerIssue$key
}

// RELAY-CLEANUP(IssueViewerIssue): the provider-free core accepts the canonical page shape and the
// temporary legacy fragment shape. Remove the legacy union member with the legacy adapter.
export type IssueViewerCoreIssue =
  | Omit<IssueNode, 'backTimelineItems' | 'timelineItems' | 'totalCommentsCount'>
  | Omit<IssueViewerIssue$data, 'timeline'>

export type IssueViewerCoreProps = IssueViewerCoreBaseProps & {
  issue: IssueViewerCoreIssue
  renderTimeline: (props: IssueTimelineRenderProps) => React.ReactNode
  timelineAvailable: boolean
  legacyQueryPreheat?: boolean
}

type IssueViewerWithSecondaryProps = IssueViewerInternalProps & {
  owner: string
  repo: string
  number: number
}

function doVariablesMatch(
  queryVariables: IssueViewerViewQuery$variables,
  expectedVariables: IssueViewerViewQuery$variables,
): boolean {
  return (
    doBaseVariablesMatch(queryVariables, expectedVariables) &&
    queryVariables.timelineItemTypes?.join() === expectedVariables.timelineItemTypes?.join()
  )
}

function doBaseVariablesMatch(
  queryVariables: IssueViewerViewQuery$variables,
  expectedVariables: IssueViewerViewQuery$variables,
): boolean {
  return (
    queryVariables.number === expectedVariables.number &&
    queryVariables.owner === expectedVariables.owner &&
    queryVariables.repo === expectedVariables.repo &&
    (queryVariables.allowedOwner ?? null) === (expectedVariables.allowedOwner ?? null) &&
    (queryVariables.hideTimeline ?? false) === (expectedVariables.hideTimeline ?? false) &&
    queryVariables.count === expectedVariables.count &&
    queryVariables.skip === expectedVariables.skip
  )
}

// `dynamic` rather than `React.lazy`: it resolves synchronously on the server, so the composer is
// in the SSR markup. Under `React.lazy` it was client-only behind a null fallback, which meant a
// chunk that failed to load left the page with no comment box at all.
const IssueCommentComposer = dynamic(
  async () => {
    const m = await import('@github-ui/commenting/IssueCommentComposer')
    return {default: m.IssueCommentComposer}
  },
  {displayName: 'IssueCommentComposer'},
)

const SubIssuesList = lazyWithPreload(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesList')
  return {default: m.SubIssuesList}
})

export function preheatIssueViewerSecondaryComponents() {
  return SubIssuesList.preload()
}

const SubIssuesCreateDialog = React.lazy(async () => {
  const m = await import('@github-ui/sub-issues/SubIssuesCreateDialog')
  return {default: m.SubIssuesCreateDialog}
})

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerViewGraphqlQuery = graphql`
  query IssueViewerViewQuery(
    $repo: String!
    $owner: String!
    $number: Int!
    $allowedOwner: String
    $hideTimeline: Boolean = false
    $skip: Int
    $count: Int
    $timelineItemTypes: [IssueTimelineItemsItemType!]
  ) {
    repository(name: $repo, owner: $owner) @catch(to: RESULT) {
      id @required(action: THROW)
      isOwnerEnterpriseManaged
      owner {
        userNameDisplayConfiguration
      }
      issue(number: $number) @catch(to: RESULT) {
        id @required(action: THROW)
        ...IssueViewerIssue
          @arguments(
            allowedOwner: $allowedOwner
            hideTimeline: $hideTimeline
            skip: $skip
            count: $count
            timelineItemTypes: $timelineItemTypes
          )
      }
    }
    safeViewer {
      isEnterpriseManagedUser
      enterpriseManagedEnterpriseId
      login
      __typename
      id
      name
      profileResourcePath
      avatarUrl(size: 64)
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const IssueViewerIssueGraphqlFragment = graphql`
  fragment IssueViewerIssue on Issue
  @argumentDefinitions(
    allowedOwner: {type: "String", defaultValue: null}
    hideTimeline: {type: "Boolean", defaultValue: false}
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
    timelineItemTypes: {type: "[IssueTimelineItemsItemType!]"}
  ) {
    id
    number
    viewerCanUpdateMetadata
    repository {
      id
      databaseId
      name
      isArchived
      nameWithOwner
      viewerCanInteract
      viewerInteractionLimitReasonHTML
      slashCommandsEnabled
      visibility
      viewerCanPush
      owner {
        __typename
        login
        id
        url
        databaseId
      }
    }
    updatedAt
    titleHTML
    url
    viewerCanUpdateNext
    repository {
      name
      isPrivate
      isWritable
      owner {
        login
      }
    }
    issueType {
      name
      color
    }
    duplicateOf {
      number
      url
      repository {
        name
        owner {
          login
        }
      }
    }
    linkedPullRequests: closedByPullRequestsReferences(first: 10, includeClosedPrs: false, orderByState: true) {
      nodes {
        repository {
          name
          nameWithOwner
          owner {
            login
          }
        }
        url
        number
        state
        isDraft
      }
    }
    agentAssignments {
      __typename
    }
    closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
      nodes {
        id
        number
        url
        state
        databaseId
      }
    }
    labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
          archivedAt
        }
      }
    }
    milestone {
      id
      title
      closed
      dueOn
      progressPercentage
      url
      closedAt
    }
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    locked
    viewerCanComment
    databaseId
    state
    stateReason
    title
    createdAt
    viewerDidAuthor
    author {
      avatarUrl
      login
      id
      profileUrl
      ... on User {
        name
      }
      ... on EnterpriseUserAccount {
        name
      }
      ... on Mannequin {
        name
      }
      __typename
      ... on Bot {
        isCopilot
        isAgent
      }
    }
    url
    viewerCanUpdateNext
    body
    bodyHTML(unfurlReferences: true)
    bodyVersion
    reactionGroups {
      content
      viewerHasReacted
      reactors(first: 10) {
        totalCount
        nodes {
          __typename
          ... on Actor {
            login
            name
            avatarUrl(size: 64)
            ... on Bot {
              isCopilot
              isAgent
              displayName
            }
          }
        }
      }
    }
    pinnedIssueComment {
      pinnedBy {
        login
      }
      issueComment {
        id
        shortBodyHTML(limit: 700, stripBlockElements: false)
        createdAt
        url
        viewerCanUnpin
        author {
          login
          avatarUrl
          profileUrl
        }
      }
    }
    id
    number
    repository {
      id
      databaseId
      name
      nameWithOwner
      visibility
      isOwnerEnterpriseManaged
      viewerCanPinIssues
      owner {
        __typename
        login
        databaseId
        issueFieldsEnabled
        ... on Organization {
          name
          issueFields(first: 1) {
            totalCount
          }
          pinnedIssueFields(first: 100) {
            nodes {
              __typename
              ... on IssueFieldText {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldSingleSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldMultiSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldDate {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldNumber {
                id
                name
                dataType
                visibility
              }
            }
          }
        }
        ... on User {
          name
          issueFields(first: 1) {
            totalCount
          }
          pinnedIssueFields(first: 100) {
            nodes {
              __typename
              ... on IssueFieldText {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldSingleSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldMultiSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldDate {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldNumber {
                id
                name
                dataType
                visibility
              }
            }
          }
        }
      }
      isArchived
      isCopilotAgentEnabled
      issueTypes(first: 100) {
        edges {
          node {
            id
          }
        }
      }
      pinnedIssues(first: 3) {
        totalCount
      }
      viewerIssueCreationPermissions {
        triageable
      }
    }
    assignedActors(first: 20) {
      nodes {
        ... on Actor {
          __typename
          id
          login
          name
          profileResourcePath
          avatarUrl(size: 64)
          ... on Bot {
            isCopilot
            isAgent
            displayName
          }
        }
      }
    }
    agentAssignments {
      assignee {
        id
        login
        avatarUrl(size: 64)
        isCopilot
        isAgent
        displayName
      }
      assigner {
        __typename
        ... on Node {
          id
        }
        ... on Actor {
          login
        }
      }
    }
    viewerCanAssign
    labels(first: 100, orderBy: {field: NAME, direction: ASC}) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          color
          name
          nameHTML
          description
          url
          archivedAt
        }
      }
    }
    viewerCanLabel
    projectItems(first: 25, allowedOwner: $allowedOwner, allowSameEnterprise: true)
      @connection(key: "ProjectSection_projectItems") {
      edges {
        node {
          id
          isArchived
          fieldValueByName(name: "Status") {
            ... on ProjectV2ItemFieldSingleSelectValue {
              id
              optionId
              name
              nameHTML
              color
            }
          }
          project {
            id
            title
            closed
            number
            url
            template
            viewerCanUpdate
            hasReachedItemsLimit
            field(name: "Status") {
              ... on ProjectV2SingleSelectField {
                databaseId
                id
                name
                options {
                  id
                  optionId
                  name
                  nameHTML
                  color
                  descriptionHTML
                  # The status field has a non unique id. We leverage the getDataID method in the Relay environment,
                  # and in this instance use the id, name, description and color to generate a unique ID:
                  # - https://github.com/github/github/pull/305841/files#diff-7187f5ed804646bc353b5176a059d44bcd6a4cd5d5f19976aebb885d1e6997b8R155
                  description
                }
              }
            }
          }
        }
      }
    }
    viewerCanUpdateMetadata
    id
    milestone {
      id
      title
      closed
      dueOn
      progressPercentage
      url
      closedAt
    }
    viewerCanSetMilestone
    number
    title
    body
    isPinned
    locked
    viewerCanDelete
    viewerCanTransfer
    viewerCanConvertToDiscussion
    viewerCanLock
    issueType {
      id
      name
      isEnabled
      description
      color
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldMultiSelect {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldDate {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
          visibility
        }
      }
    }
    viewerCanType
    viewerCanSetFields
    issueFieldValues(first: 25) {
      nodes {
        ... on IssueFieldTextValue {
          field {
            ... on IssueFieldText {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldSingleSelectValue {
          field {
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
              visibility
            }
          }
          optionId
          name
          color
          description
        }
        ... on IssueFieldMultiSelectValue {
          field {
            ... on IssueFieldMultiSelect {
              id
              name
              dataType
              visibility
            }
          }
          options {
            id
            name
            color
            description
          }
          multiSelectValue: value
        }
        ... on IssueFieldDateValue {
          field {
            ... on IssueFieldDate {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldNumberValue {
          field {
            ... on IssueFieldNumber {
              id
              name
              dataType
              visibility
            }
          }
          numberValue: value
        }
      }
    }
    ...IssueTimelineIssueFragment
      @alias(as: "timeline")
      @skip(if: $hideTimeline)
      @arguments(skip: $skip, count: $count, itemTypes: $timelineItemTypes)
    # sub-issues
    subIssuesSummary {
      total
      completed
    }
    pendingSuggestions {
      __typename
      ... on PendingTypeSuggestion {
        id
        typeState: state
        createdAt
        intent {
          intentId
        }
        actor {
          __typename
          login
          name
          avatarUrl
          resourcePath
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        issueType {
          id
          name
          color
        }
      }
      ... on PendingLabelSuggestion {
        id
        labelState: state
        createdAt
        intent {
          intentId
        }
        actor {
          __typename
          login
          name
          avatarUrl
          resourcePath
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        label {
          id
          name
          color
        }
      }
      ... on PendingFieldSuggestion {
        id
        fieldState: state
        createdAt
        intent {
          intentId
        }
        actor {
          __typename
          login
          name
          avatarUrl
          resourcePath
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        suggestedValue
        confidence
        issueField {
          __typename
          ... on Node {
            id
          }
          ... on IssueFieldCommon {
            name
          }
          ... on IssueFieldSingleSelect {
            options {
              name
              color
            }
          }
        }
      }
      ... on PendingCloseSuggestion {
        id
        closeState: state
        createdAt
        intent {
          intentId
        }
        actor {
          __typename
          login
          name
          avatarUrl
          resourcePath
          id
          ... on Bot {
            displayName
          }
        }
        rationale
        confidence
        stateReason
        duplicateOf {
          __typename
          ... on Issue {
            number
            title
            url
            repository {
              nameWithOwner
            }
          }
          ... on PullRequest {
            number
            title
            url
            repository {
              nameWithOwner
            }
          }
        }
      }
      ... on PendingAssigneeSuggestion {
        id
        assigneeState: state
        createdAt
        intent {
          intentId
        }
        actor {
          __typename
          login
          name
          avatarUrl
          resourcePath
          id
          ... on Bot {
            displayName
          }
        }
        confidence
        rationale
        assignee {
          __typename
          ... on Node {
            id
          }
          ... on Actor {
            login
            displayName
            avatarUrl
          }
          ... on Bot {
            isAgent
          }
        }
      }
    }
  }
`

export function IssueViewer({
  itemIdentifier,
  optionConfig = ISSUE_VIEWER_DEFAULT_CONFIG,
  linkedArtifactNumber,
}: IssueViewerProps) {
  const {repo, owner, number} = itemIdentifier
  const urlQuery = ssrSafeLocation.search
  const searchParams = new URLSearchParams(urlQuery)
  const timelinePage = searchParams.get('timeline_page')

  const [issueViewerViewRef, loadIssueViewerView] = useCacheQueryLoader<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    DEFAULT_RELAY_CACHE_TTL,
    ['allowedOwner'],
  )
  const issueViewerContainerRef = useIssueViewerContainerQueryFix(owner, repo, number)

  const fetchPolicy = optionConfig.issueQueriesFetchingPolicy?.fetchPolicy

  const expectedVariables = useMemo<IssueViewerViewQuery$variables>(
    () =>
      buildIssueViewerViewQueryVariables({
        owner,
        repo,
        number,
        allowedOwner: optionConfig.allowedProjectOwner,
        hideTimeline: optionConfig.hideTimeline,
      }),
    [owner, repo, number, optionConfig.allowedProjectOwner, optionConfig.hideTimeline],
  )

  useEffect(() => {
    const variablesMatch = issueViewerViewRef && doVariablesMatch(issueViewerViewRef.variables, expectedVariables)
    const serverPreloadedQuery = optionConfig.preloadedQueries?.issueViewerViewQuery
    const compatibleServerPreload =
      issueViewerViewRef != null &&
      serverPreloadedQuery != null &&
      doVariablesMatch(issueViewerViewRef.variables, serverPreloadedQuery.variables) &&
      doBaseVariablesMatch(issueViewerViewRef.variables, expectedVariables)

    if (!issueViewerViewRef || (!variablesMatch && !compatibleServerPreload)) {
      loadIssueViewerView(expectedVariables, {
        fetchPolicy,
      })
    }
  }, [
    loadIssueViewerView,
    optionConfig.preloadedQueries?.issueViewerViewQuery,
    fetchPolicy,
    issueViewerViewRef,
    expectedVariables,
  ])

  // Reset scroll position to top when a new issue loads inside the side panel.
  // Firefox preserves the scroll position of the overflow container across re-renders,
  // causing the new issue to appear scrolled partway down.
  useSidePanelScrollReset(optionConfig.insideSidePanel, owner, repo, number)

  const retryQuery = useCallback(
    () => loadIssueViewerView(expectedVariables, {fetchPolicy: 'network-only'}),
    [loadIssueViewerView, expectedVariables],
  )

  if (!issueViewerViewRef) return <IssueViewerLoading optionConfig={optionConfig} />

  // Check if the query ref variables match the requested issue - needed for fast viewer issue navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  return (
    <PreloadedQueryBoundary
      key={`${owner}-${repo}-${number}`}
      onRetry={retryQuery}
      fallback={(retry, error) =>
        renderIssueViewerErrors(retry, error, optionConfig.insideSidePanel ? optionConfig.onClose : undefined)
      }
      critical
    >
      <div
        className={clsx(
          styles.issueViewerContainer,
          styles.mainContainer,
          isFeatureEnabled('issues_sticky_header') && styles.mainContainer_stickyHeader,
        )}
        ref={issueViewerContainerRef}
      >
        <Suspense fallback={<IssueViewerLoading optionConfig={optionConfig} />}>
          <IsssueViewerWithSecondary
            owner={owner}
            repo={repo}
            number={number}
            issueViewerViewRef={issueViewerViewRef}
            optionConfig={optionConfig}
            retryQuery={retryQuery}
            timelinePage={(timelinePage && parseInt(timelinePage, 10)) || undefined}
            linkedArtifactNumber={linkedArtifactNumber}
          />
        </Suspense>
      </div>
    </PreloadedQueryBoundary>
  )
}

function IssueViewerInternal({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  secondaryIssueData,
  initialNestedSubIssuesData,
  initialNestedSubIssuesDataUpdatedAt,
  hideClosedSubIssuesEnabled,
  subIssueDeletionConnection,
  timelinePage,
  issueUpdatesChannel,
  linkedArtifactNumber,
  retryQuery,
}: IssueViewerInternalProps) {
  const {repository, safeViewer} = usePreloadedQuery<IssueViewerViewQuery>(
    IssueViewerViewGraphqlQuery,
    issueViewerViewRef,
  )

  // ensure query ref variables still match after Suspense resolves
  // This catches race conditions during rapid navigation where data may become stale
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  const repositoryResult = handleRepositoryDataError(repository, {
    shouldReloadOnError: !optionConfig.insideSidePanel,
    subIssueFromOptimisticUpdate: optionConfig.subIssueFromOptimisticUpdate,
  })
  if (!repositoryResult || !repositoryResult.value) {
    if (optionConfig.subIssueFromOptimisticUpdate && retryQuery) {
      return <OptimisticUpdateRetry retryQuery={retryQuery} optionConfig={optionConfig} />
    }
    if (optionConfig.insideSidePanel) {
      return <NotFoundError onClose={optionConfig.onClose} />
    }
    return null
  }

  if (repositoryResult.value.issue?.ok === false) {
    const errors = repositoryResult.value.issue.errors
    const hasNotFoundError = errors?.some(e => e && typeof e === 'object' && 'type' in e && e.type === 'NOT_FOUND')

    if (hasNotFoundError) {
      if (optionConfig.subIssueFromOptimisticUpdate && retryQuery) {
        return <OptimisticUpdateRetry retryQuery={retryQuery} optionConfig={optionConfig} />
      }
      if (!optionConfig.insideSidePanel) {
        // if the issue is not found, reload the page to get a fresh 404 or deleted issue page from the server
        // only if we are viewing the issue in the issue show page (not in a side panel)
        reloadPage()
        return null
      }
      return <NotFoundError onClose={optionConfig.onClose} />
    }
    throw new Error(`IssueViewer: repository.issue is errored. Errors: ${JSON.stringify(errors)}`)
  }

  if (!repositoryResult.value.issue?.value) {
    return null
  }

  return (
    <IssueAgentTasksProvider owner={owner} repo={repo} issueNumber={number}>
      <UserNameDisplayContextProvider value={repositoryResult.value.owner.userNameDisplayConfiguration}>
        <ArtifactsPanelProvider>
          <ArtifactsPanelLayout insideSidePanel={optionConfig.insideSidePanel}>
            <IssueViewerInternalFragment
              viewer={safeViewer || null}
              issueFragment={repositoryResult.value.issue.value}
              feed={issueViewerFeedFromTimelineItemTypes(issueViewerViewRef.variables.timelineItemTypes)}
              optionConfig={optionConfig}
              isRepoOwnerEnterpriseManaged={repositoryResult.value.isOwnerEnterpriseManaged}
              secondaryIssueData={secondaryIssueData}
              initialNestedSubIssuesData={initialNestedSubIssuesData}
              initialNestedSubIssuesDataUpdatedAt={initialNestedSubIssuesDataUpdatedAt}
              hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
              subIssueDeletionConnection={subIssueDeletionConnection}
              timelinePage={timelinePage}
              issueUpdatesChannel={issueUpdatesChannel}
              linkedArtifactNumber={linkedArtifactNumber}
            />
          </ArtifactsPanelLayout>
        </ArtifactsPanelProvider>
      </UserNameDisplayContextProvider>
    </IssueAgentTasksProvider>
  )
}

export function getPermalinkType(hash: string): 'issuecomment' | 'event' | 'cross_reference' | null {
  const hashWithoutSymbol = hash.startsWith('#') ? hash.substring(1) : hash
  if (!hashWithoutSymbol) return null
  if (hashWithoutSymbol.startsWith('issuecomment-')) return 'issuecomment'
  if (hashWithoutSymbol.startsWith('event-')) return 'event'
  if (hashWithoutSymbol.startsWith('ref-issue-') || hashWithoutSymbol.startsWith('ref-pullrequest-'))
    return 'cross_reference'
  return null
}

export function initialTimelineFeed(hash: string): TimelineFeed {
  const permalinkType = getPermalinkType(hash)
  return permalinkType === 'event' || permalinkType === 'cross_reference' ? 'activity' : 'comments'
}

type TimelineFeedContentProps = IssueTimelineRenderProps & {
  hideTimeline: boolean
  isPrimaryFeed?: boolean
  timelineAvailable: boolean
  renderTimeline: IssueViewerCoreProps['renderTimeline']
}

function TimelineFeedContent({
  hideTimeline,
  isPrimaryFeed = false,
  timelineAvailable,
  renderTimeline,
  viewer,
  ...timelineProps
}: TimelineFeedContentProps) {
  return (
    <div
      data-testid={isPrimaryFeed ? TEST_IDS.issueViewerCommentsContainer : undefined}
      className={isPrimaryFeed ? CLASS_NAMES.commentsContainer : undefined}
    >
      <div className={styles.commentsContainer}>
        {!hideTimeline && timelineAvailable && renderTimeline({viewer, ...timelineProps})}
        {!viewer && isPrimaryFeed && <SignedOutBanner />}
      </div>
    </div>
  )
}

export function resolveHasSubIssues(
  primaryHasSubIssues: boolean,
  secondaryIssueData:
    | {readonly subIssues?: {readonly nodes?: ReadonlyArray<unknown | null> | null} | null}
    | null
    | undefined,
  secondarySubIssuesPresenceEnabled: boolean,
): boolean {
  if (!secondarySubIssuesPresenceEnabled || secondaryIssueData == null) return primaryHasSubIssues
  return (secondaryIssueData.subIssues?.nodes ?? []).some(node => node != null)
}

export function IssueViewerInternalFragment({issueFragment, ...props}: IssueViewerInternalFragmentProps) {
  const fragmentIssue = useFragment(IssueViewerIssueGraphqlFragment, issueFragment)
  const timelineIssue = fragmentIssue?.timeline ?? null
  const renderTimeline = useCallback(
    (timelineProps: IssueTimelineRenderProps) => <IssueTimeline {...timelineProps} issue={timelineIssue} />,
    [timelineIssue],
  )

  if (!fragmentIssue) return null

  return (
    <IssueViewerCore
      {...props}
      issue={fragmentIssue}
      renderTimeline={renderTimeline}
      timelineAvailable={timelineIssue !== null}
      legacyQueryPreheat
    />
  )
}

export function IssueViewerCore({
  viewer,
  issue: primaryIssue,
  renderTimeline,
  timelineAvailable,
  legacyQueryPreheat,
  feed,
  initialBackAnchor,
  optionConfig,
  isRepoOwnerEnterpriseManaged,
  secondaryIssueData,
  initialNestedSubIssuesData,
  initialNestedSubIssuesDataUpdatedAt,
  hideClosedSubIssuesEnabled,
  subIssueDeletionConnection,
  timelinePage,
  issueUpdatesChannel,
  linkedArtifactNumber,
  routeQueryKey,
}: IssueViewerCoreProps) {
  const primaryIdentifier = useMemo(
    () =>
      optionConfig.accountIssueOption?.isAccountIssue
        ? {permalink: optionConfig.accountIssueOption.permalink}
        : {
            owner: primaryIssue.repository.owner.login,
            repo: primaryIssue.repository.name,
            number: primaryIssue.number,
          },
    [optionConfig.accountIssueOption, primaryIssue.number, primaryIssue.repository],
  )
  const primaryReconciliation = useMemo(
    () => ({identifier: primaryIdentifier, queryKey: routeQueryKey}),
    [primaryIdentifier, routeQueryKey],
  )
  const {issue} = useIssueMetadata(
    primaryIssue,
    optionConfig.allowedProjectOwner,
    issueUpdatesChannel,
    primaryReconciliation,
  )
  const [currentHash, , hashHydrated] = useHash()
  const [initialHash, setInitialHash] = useState<string | null>(null)
  const sharedSubIssuesData = useSubIssuesData({
    issue: secondaryIssueData,
    parentIssueId: issue.id,
    owner: issue.repository.owner.login,
    repo: issue.repository.name,
    number: issue.number,
    initialData: initialNestedSubIssuesData,
    initialDataUpdatedAt: initialNestedSubIssuesDataUpdatedAt,
    updatesChannel: issueUpdatesChannel,
  })

  // Fire onLoad callback when issue data is available (component has mounted after Suspense resolved)
  const onLoad = optionConfig.onLoad
  useEffect(() => {
    onLoad?.()
  }, [onLoad])

  const primaryHasSubIssues = useHasSubIssues(issue)
  // Gate the section on the rows the TanStack list actually renders (non-null nodes), not the
  // Relay `subIssuesSummary.total`, which lags and can keep the section visible at zero. Fall
  // back to the summary until the non-Suspense secondary query resolves, so it doesn't vanish.
  const hasSubIssues = resolveHasSubIssues(primaryHasSubIssues, sharedSubIssuesData ?? secondaryIssueData, true)
  const canEditSubIssues = useCanEditSubIssues(secondaryIssueData)
  const {hideTimeline = false} = optionConfig
  // Follows the rendered feed, which also reflects org rollouts the client flag misses.
  const activityTabEnabled = feed === 'comments'
  const showActivityTab = activityTabEnabled && !hideTimeline
  const currentPermalinkType = getPermalinkType(currentHash)

  if (hashHydrated && initialHash === null) {
    setInitialHash(currentHash)
  }

  useEffect(() => {
    if (initialHash === null) return

    const permalinkType = getPermalinkType(initialHash)
    if (permalinkType) sendEvent('issue_viewer.permalink.viewed', {permalink_type: permalinkType})
  }, [initialHash])

  // A page view with Comments and Activity split. The viewer remounts for each issue, so this sends once per issue.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- a page view has no handler to send it from.
    if (initialHash === null || !showActivityTab) return

    sendEvent('issue_viewer.comments.viewed', {permalink_type: getPermalinkType(initialHash)})
  }, [initialHash, showActivityTab])

  const showEmuContributionBlockedBanner =
    viewer && !!viewer.enterpriseManagedEnterpriseId && !isRepoOwnerEnterpriseManaged
  const showCommentComposer = viewer && !showEmuContributionBlockedBanner && !optionConfig.isReadonly
  // Crawler pages (?timeline_page=) always stay oldest first, and Activity stays unfiltered.
  const commentsSortable = showActivityTab && !timelinePage
  const {sort: commentsSort, setSort: setCommentsSort} = useIssueCommentsSort(commentsSortable)
  const commentComposerAtTop = commentsSort === ChronologicalCommentsSort.NewestFirst
  const activityHighlightedEvent =
    currentPermalinkType === 'issuecomment' ? undefined : getHighlightedEventText(currentHash, ISSUE_EVENTS)
  const activityView = useActivityTimelineView({
    enabled: commentsSortable,
    highlightedEvent: activityHighlightedEvent,
    commentsSort,
  })
  const activityFilterButtonRef = useRef<HTMLButtonElement>(null)
  const commentsHeadingId = useId()
  const issueArtifactsResourceId = secondaryIssueData?.databaseId ?? 0
  const scribeArtifactsApiEnabled = useScribeArtifactsApiEnabled()
  const [issueArtifactsPagination, setIssueArtifactsPagination] = useState({resourceId: 0, page: 1})
  const issueArtifactsPage =
    issueArtifactsPagination.resourceId === issueArtifactsResourceId ? issueArtifactsPagination.page : 1
  const {data: issueArtifactsPayload, isPlaceholderData: isIssueArtifactsPlaceholderData} = useScribeArtifacts({
    resourceType: 'issue',
    resourceId: issueArtifactsResourceId,
    page: issueArtifactsPage,
    perPage: ISSUE_ARTIFACTS_PER_PAGE,
    enabled: scribeArtifactsApiEnabled && shouldFetchIssueArtifacts(issueArtifactsResourceId),
  })
  const issueArtifacts = issueArtifactsPayload?.artifacts
  const [pendingArtifactPageFocus, setPendingArtifactPageFocus] = useState<{resourceId: number; page: number} | null>(
    null,
  )
  const handleIssueArtifactsPageChange = useCallback(
    (page: number) => {
      setPendingArtifactPageFocus({resourceId: issueArtifactsResourceId, page})
      setIssueArtifactsPagination({resourceId: issueArtifactsResourceId, page})
    },
    [issueArtifactsResourceId],
  )
  const showCreateArtifactButton =
    scribeArtifactsApiEnabled && shouldShowCreateArtifactButton(issueArtifacts, issueArtifactsPage)
  const showArtifactsSection =
    scribeArtifactsApiEnabled && shouldShowArtifactsSection(issueArtifacts, issueArtifactsPage)
  const [focusArtifactsSectionOnShow, setFocusArtifactsSectionOnShow] = useState(false)
  const [focusCreateArtifactButtonOnShow, setFocusCreateArtifactButtonOnShow] = useState(false)
  const focusArtifactsSectionAfterPageLoad =
    pendingArtifactPageFocus?.resourceId === issueArtifactsResourceId &&
    pendingArtifactPageFocus.page === issueArtifactsPage &&
    !isIssueArtifactsPlaceholderData
  const handleInitialArtifactCreated = useCallback(() => {
    setFocusArtifactsSectionOnShow(true)
  }, [])
  const handleArtifactsSectionFocusTargetReady = useCallback(() => {
    setFocusArtifactsSectionOnShow(false)
    setPendingArtifactPageFocus(null)
  }, [])
  const handleLastArtifactDeleted = useCallback(() => {
    if (issueArtifactsPage > 1) {
      handleIssueArtifactsPageChange(issueArtifactsPage - 1)
      return
    }
    setFocusCreateArtifactButtonOnShow(true)
  }, [handleIssueArtifactsPageChange, issueArtifactsPage])
  const handleCreateArtifactButtonFocusTargetReady = useCallback(() => {
    setFocusCreateArtifactButtonOnShow(false)
  }, [])

  const effectiveOptionConfig = useMemo(
    () => (showEmuContributionBlockedBanner ? {...optionConfig, showIssueCreateButton: false} : optionConfig),
    [optionConfig, showEmuContributionBlockedBanner],
  )
  const configuredOnSubIssueClick = optionConfig.onSubIssueClick
  const notifySubIssueClick = useCallback<NonNullable<OptionConfig['onSubIssueClick']>>(
    (item, options) => configuredOnSubIssueClick?.(item, options),
    [configuredOnSubIssueClick],
  )
  const handleSubIssueClick = useCallback(
    (item: SubIssueSidePanelItem, fromSuccessfulOptimisticUpdate?: boolean) => {
      const parent = item.parentIssue
      const issueDeletionConnection: IssueDeletionConnection | undefined = parent
        ? {
            ownerId: parent.id,
            connectionPath: ['subIssues'],
            targets: [
              {
                queryKey: issueViewerSecondaryViewQueryKey(parent.owner, parent.repo, parent.number),
                revalidate: true,
                refetchType: 'all',
              },
              {queryKey: nestedSubIssuesQueryKey(parent.owner, parent.repo, parent.number)},
            ],
          }
        : subIssueDeletionConnection
      notifySubIssueClick(item, {
        ...(fromSuccessfulOptimisticUpdate === undefined ? {} : {fromSuccessfulOptimisticUpdate}),
        ...(issueDeletionConnection ? {issueDeletionConnection} : {}),
      })
    },
    [notifySubIssueClick, subIssueDeletionConnection],
  )

  const composerRef = useRef<MarkdownComposerRef>(null)
  const issueBodyRef = useRef<HTMLDivElement>(null)
  const {startCommentEdit, cancelCommentEdit, isCommentEditActive} = useCommentEditsContext()
  const issueBodyKey = `issue-${issue.id}-body`
  const {setInputElementState, clearInputElementStates} = useInputElementActiveContext()
  const {anyItemPickerOpen} = useItemPickersContext()

  // Reset comment and input states on issue change
  useEffect(() => {
    clearInputElementStates()
  }, [issue.id, clearInputElementStates])

  useEffect(() => {
    setInputElementState(IDS.itemPicker, anyItemPickerOpen())
  }, [anyItemPickerOpen, setInputElementState])

  // Whenever the issue body editing state changes, synchronize that state via callbacks
  const onIssueEditStateChange = optionConfig.onIssueEditStateChange
  useEffect(() => {
    onIssueEditStateChange?.(isCommentEditActive())
  }, [isCommentEditActive, onIssueEditStateChange])

  const onCommentReply = useCallback((quotedComment: string) => {
    composerRef.current?.setText(quotedComment)
    requestAnimationFrame(() => composerRef.current?.focus())
  }, [])

  const onCommentChange = useCallback(
    (id: string) => {
      startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditStart?.(id)
    },
    [issue.id, optionConfig, startCommentEdit],
  )
  const onCommentEditCancel = useCallback(
    (id: string) => {
      cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueComment('', issue.id, id))
      optionConfig.onCommentEditCancel?.(id)
    },
    [cancelCommentEdit, issue.id, optionConfig],
  )

  const beforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (isCommentEditActive()) {
        event.preventDefault()
        return (event.returnValue = '')
      }
    },
    [isCommentEditActive],
  )

  useBeforeUnload(beforeUnload)

  const metadataContent = useMemo(
    () => (
      <IssueSidebar
        // RELAY-CLEANUP(IssueViewerIssue): open-enum fields from the still-Relay parent.
        // Remove when IssueViewerIssue leaves Relay.
        issue={issue as unknown as IssueSidebarPrimaryFragment}
        sidebarSecondaryKey={secondaryIssueData}
        lazySecondaryData={secondaryIssueData}
        viewer={viewer}
        optionConfig={optionConfig}
        routeQueryKey={routeQueryKey}
      />
    ),
    [issue, optionConfig, routeQueryKey, secondaryIssueData, viewer],
  )

  const metadataPane = useMemo(
    () => (
      <div className={styles.issueViewerMetadataPane} data-testid={TEST_IDS.issueViewerMetadataPane}>
        {isFeatureEnabled('issues_sticky_header') ? (
          <Heading as="h2" className="sr-only">
            {LABELS.metadataHeader}
          </Heading>
        ) : (
          <>
            <Heading as="h2" className={styles.metadataHeader}>
              {LABELS.metadataHeader}
            </Heading>
            <Heading as="h2" className={`${styles.largeScreenMetadataHeader} sr-only`}>
              {LABELS.metadataHeader}
            </Heading>
          </>
        )}
        {metadataContent}
      </div>
    ),
    [metadataContent],
  )

  const handleBodyEditStateChange = useCallback(
    (isEditing: boolean) => {
      optionConfig?.onIssueEditStateChange?.(isEditing)
      if (isEditing) {
        startCommentEdit(issueBodyKey)
      } else {
        cancelCommentEdit(issueBodyKey)
      }
    },
    [cancelCommentEdit, issueBodyKey, optionConfig, startCommentEdit],
  )
  const {createDialogOpen, activeIssueId, closeCreateDialog} = useSubIssueState()

  const timelineConnectionIds = useMemo(() => {
    return buildTimelineConnectionIds(issue.id)
  }, [issue.id])

  const {isSticky: isHeaderSticky, sentinelRef} = useStickyObserver()
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  const [sidePanelOpen, setSidePanelOpen] = useState(false)
  const sidePanelReturnFocusRef = useRef<HTMLButtonElement>(null)

  // Expose the sticky header's measured height (plus any notification-shelf offset) as a CSS
  // variable on the viewer grid so descendant sticky elements (e.g. the sub-issues list header)
  // can stick flush against the header instead of relying on a hardcoded height.
  const viewerGridRef = useRef<HTMLDivElement>(null)
  const [stickyHeaderNode, setStickyHeaderNode] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!stickyHeaderNode || typeof ResizeObserver === 'undefined') return
    let raf = 0
    let last = -1
    const observer = new ResizeObserver(([entry]) => {
      const height = entry?.borderBoxSize?.[0]?.blockSize ?? entry?.contentRect.height ?? 0
      if (height === last) return
      last = height
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        viewerGridRef.current?.style.setProperty(
          '--issue-sticky-header-offset',
          `calc(var(--base-sticky-header-height, 0px) + ${height}px)`,
        )
      })
    })
    observer.observe(stickyHeaderNode)
    return () => {
      cancelAnimationFrame(raf)
      observer.disconnect()
    }
  }, [stickyHeaderNode])

  const issuePath = `/${issue.repository.owner.login}/${issue.repository.name}/issues/${issue.number}`
  const defaultIssueUrl = ssrSafeLocation.origin ? `${ssrSafeLocation.origin}${issuePath}` : issuePath
  const issueUrl = canonicalIssueUrl(defaultIssueUrl, optionConfig.accountIssueOption)

  // RELAY-CLEANUP(IssueViewerIssue): open enum `state`/`stateReason` from still-Relay parent.
  // Remove when IssueViewerIssue leaves Relay.
  const sharedHeaderData = useIssueHeaderRefetch({
    issue: issue as HeaderFragment,
    headerSecondary: secondaryIssueData,
    issueUpdatesChannel,
    primaryReconciliation,
  })

  const timelineProps = {
    renderTimeline,
    issueBodyRef,
    issueSecondary: secondaryIssueData,
    viewer: viewer?.login ?? null,
    owner: issue.repository.owner.login,
    repo: issue.repository.name,
    number: issue.number,
    primaryFeed: feed,
    primaryQueryKey: routeQueryKey,
    primaryIdentifier,
    onCommentReply,
    onCommentChange,
    onCommentEditCancel,
    optionConfig,
    page: timelinePage,
    issueUpdatesChannel,
  }
  const commentsFeed = feed
  const commentsTimeline = (
    <TimelineConnectorContext value={!activityTabEnabled}>
      <TimelineFeedContent
        {...timelineProps}
        feed={commentsFeed}
        initialBackAnchor={initialBackAnchor}
        commentsHeadingId={showActivityTab ? commentsHeadingId : undefined}
        highlightedEvent={
          commentsFeed === 'comments' && currentPermalinkType !== 'issuecomment'
            ? undefined
            : getHighlightedEventText(currentHash, ISSUE_EVENTS)
        }
        hideTimeline={hideTimeline}
        timelineAvailable={timelineAvailable}
        sort={commentsSort}
        loadingState={
          commentsSortable ? <IssueTimelineLoading label={VIEWER_LABELS.timeline.loadingComments} /> : undefined
        }
        isPrimaryFeed
      />
    </TimelineConnectorContext>
  )
  const activityMenu = showActivityTab ? (
    <TimelineActivityMenu
      initiallyOpen={initialHash === null ? null : initialTimelineFeed(initialHash) === 'activity'}
      legacyHeader={!stickyHeaderEnabled}
      timelineKey={activityView.itemTypes?.join(',')}
      headerActions={
        commentsSortable ? (
          <>
            <ActivityFilterMenu
              filters={activityView.filters}
              onFiltersChange={activityView.setFilters}
              anchorRef={activityFilterButtonRef}
            />
            <ActivitySortMenu sort={activityView.sort} onSortChange={activityView.setSort} />
          </>
        ) : undefined
      }
      notice={
        activityView.filtersSetAside ? (
          <ActivityFiltersSetAsideNotice
            onRestore={activityView.restoreFilters}
            returnFocusRef={activityFilterButtonRef}
          />
        ) : undefined
      }
      timeline={
        <TimelineFeedContent
          {...timelineProps}
          feed="activity"
          itemTypes={activityView.itemTypes}
          emptyState={
            activityView.itemTypes ? (
              <ActivityFilterEmptyState
                onClearFilters={() => activityView.setFilters([])}
                returnFocusRef={activityFilterButtonRef}
              />
            ) : (
              <ActivityEmptyState />
            )
          }
          loadingState={<ActivityLoadingState />}
          initialBackAnchor={undefined}
          highlightedEvent={activityView.timelineHighlightedEvent}
          hideTimeline={hideTimeline}
          timelineAvailable={timelineAvailable}
          sort={activityView.sort}
        />
      }
    />
  ) : null
  // Keyed so the composer keeps its state when the sort moves it above or below the comments.
  const commentComposer = showCommentComposer && (
    <ErrorBoundary
      key="comment-composer"
      boundaryName="issue-comment-composer"
      fallback={<IssueCommentComposerErrorFallback />}
      critical
    >
      <Suspense fallback={null}>
        <IssueCommentComposer
          ref={composerRef}
          issue={
            {
              ...issue,
              state: sharedHeaderData.state,
              stateReason: sharedHeaderData.stateReason,
            } as IssueCommentComposerFragment
          }
          issueSecondary={
            secondaryIssueData && {
              ...secondaryIssueData,
              viewerCanClose: sharedHeaderData.viewerCanClose,
              viewerCanReopen: sharedHeaderData.viewerCanReopen,
              viewerCanSetStatus: sharedHeaderData.viewerCanSetStatus,
              status: sharedHeaderData.status,
            }
          }
          viewer={viewer}
          onChange={() => {
            startCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditStart?.(IDS.newComment)
          }}
          onSave={() => {
            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditCancel?.(IDS.newComment)
          }}
          onCancel={() => {
            cancelCommentEdit(COMMENTING_VALUES.localStorageKeys.issueNewComment('viewer', issue.id))
            optionConfig.onCommentEditCancel?.(IDS.newComment)
          }}
          onNewIssueComment={optionConfig.onNewIssueComment}
          onStateChange={optionConfig.onIssueUpdate}
          fileUploadsEnabled
          insideSidePanel={optionConfig.insideSidePanel ?? false}
          placement={commentComposerAtTop ? 'top' : 'bottom'}
          headingHidden={activityTabEnabled}
        />
      </Suspense>
    </ErrorBoundary>
  )

  const issueContentArea = (
    <div
      className={clsx(
        styles.contentArea,
        optionConfig.useViewportQueries ? styles.contentArea_viewport : styles.contentArea_container,
      )}
    >
      <RecentAgentActivity
        issue={issue as RecentAgentActivityFragment}
        // This panel renders above the issue body, so waiting for the secondary query would push
        // the body down once the permission arrives. Write access is a close enough proxy to paint
        // immediately, and the real permission overrides it as soon as it resolves.
        viewerCanUpdateMetadata={secondaryIssueData?.viewerCanUpdateMetadata ?? issue.repository.isWritable}
        issueBodyRef={issueBodyRef}
        issueNumber={issue.number}
        viewerId={viewer?.id}
      />
      <div ref={issueBodyRef} data-testid={TEST_IDS.issueViewerIssueContainer} tabIndex={-1}>
        <SubIssuesPresenceContext value={hasSubIssues}>
          <IssueBody
            // RELAY-CLEANUP(IssueViewerIssue): cast bridges the still-Relay IssueViewerIssue
            // $data to the migrated plain-props fragment. Remove when IssueViewerIssue leaves Relay.
            issue={issue as IssueBodyFragment}
            issueBodySecondary={secondaryIssueData}
            secondaryKey={secondaryIssueData}
            onLinkClick={optionConfig.onLinkClick}
            onIssueEditStateChange={handleBodyEditStateChange}
            onIssueUpdate={optionConfig.onIssueUpdate}
            onCommentReply={onCommentReply}
            isIssueEditActive={isCommentEditActive}
            highlightedEventText={currentHash}
            insideSidePanel={optionConfig.insideSidePanel}
            isReadonly={optionConfig.isReadonly || false}
            issueBodyTaskListAction={
              showCreateArtifactButton ? (
                <CreateArtifactMenuButton
                  resourceId={issueArtifactsResourceId}
                  focusOnMount={focusCreateArtifactButtonOnShow}
                  onCreated={handleInitialArtifactCreated}
                  onFocusTargetReady={handleCreateArtifactButtonFocusTargetReady}
                />
              ) : undefined
            }
            issueUpdatesChannel={issueUpdatesChannel}
            primaryReconciliation={primaryReconciliation}
          />
        </SubIssuesPresenceContext>
      </div>
      {hasSubIssues && (
        <div
          data-testid={TEST_IDS.subIssuesIssueContainer}
          className={clsx(
            styles.subIssuesContainer,
            !isFeatureEnabled('issue_inline_avatars') && styles.subIssuesContainer_container,
          )}
        >
          <Suspense fallback={null}>
            <SubIssuesList
              issue={secondaryIssueData}
              parentIssueId={issue.id}
              subIssuesSummary={issue.subIssuesSummary}
              hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
              onSubIssueClick={optionConfig.onSubIssueClick ? handleSubIssueClick : undefined}
              insideSidePanel={optionConfig.insideSidePanel}
              readonly={!canEditSubIssues || optionConfig.isReadonly}
              queryToPreheat={legacyQueryPreheat ? IssueViewerViewGraphqlQuery : undefined}
            />
          </Suspense>
        </div>
      )}
      {showArtifactsSection && (
        <ArtifactsSection
          resourceId={issueArtifactsResourceId}
          repositoryId={issue.repository.databaseId ?? undefined}
          organizationId={
            issue.repository.owner.__typename === 'Organization'
              ? (issue.repository.owner.databaseId ?? undefined)
              : undefined
          }
          artifacts={issueArtifacts ?? []}
          canReorder={
            !!secondaryIssueData?.viewerCanUpdateMetadata && issue.repository.isWritable && !optionConfig.isReadonly
          }
          currentPage={issueArtifactsPage}
          hasNextPage={!isIssueArtifactsPlaceholderData && (issueArtifactsPayload?.has_next_page ?? false)}
          onPageChange={handleIssueArtifactsPageChange}
          focusOnMount={focusArtifactsSectionOnShow || focusArtifactsSectionAfterPageLoad}
          onFocusTargetReady={handleArtifactsSectionFocusTargetReady}
          onLastArtifactDeleted={handleLastArtifactDeleted}
        />
      )}
      {showActivityTab && (
        <CommentsHeader
          headingId={commentsHeadingId}
          sort={commentsSortable ? commentsSort : undefined}
          onSortChange={setCommentsSort}
          trailingControl={activityMenu}
        />
      )}
      {commentComposerAtTop && commentComposer}
      <ErrorBoundary boundaryName="issue-comments-timeline" fallback={<IssueTimelineErrorFallback />} critical>
        <Suspense fallback={<IssueTimelineLoading delayedShow />}>{commentsTimeline}</Suspense>
      </ErrorBoundary>
      {!commentComposerAtTop && commentComposer}
      {showEmuContributionBlockedBanner && <EmuContributionBlockedBanner />}
    </div>
  )

  return (
    <SubIssuesDataContext value={sharedSubIssuesData}>
      <IssueHeaderDataContext value={sharedHeaderData}>
        <TimelineConnectionProvider value={timelineConnectionIds}>
          {scribeArtifactsApiEnabled && (
            <IssueArtifactsAliveUpdates channel={issueUpdatesChannel} resourceId={issueArtifactsResourceId} />
          )}
          {linkedArtifactNumber !== undefined && (
            <LinkedArtifact
              key={linkedArtifactNumber}
              number={linkedArtifactNumber}
              resourceId={issueArtifactsResourceId}
              issueUrl={issue.url}
            />
          )}
          {optionConfig.copilotSurveyBanner && <CopilotSurvey {...optionConfig.copilotSurveyBanner} />}
          {secondaryIssueData?.discussion && (
            <ConvertedToDiscussionBanner discussionUrl={secondaryIssueData?.discussion?.url ?? ''} />
          )}
          {stickyHeaderEnabled ? (
            <>
              <StickyHeader
                issueKey={secondaryIssueData}
                issueType={issue.issueType}
                linkedSecurityAlertsData={secondaryIssueData}
                trackedBySecondaryData={secondaryIssueData}
                subIssueSecondaryData={secondaryIssueData}
                issueNumber={issue.number}
                ownerLogin={issue.repository.owner.login}
                repoName={issue.repository.name}
                issueUrl={issueUrl}
                optionConfig={effectiveOptionConfig}
                isHeaderSticky={isHeaderSticky}
                onInfoClick={() => setSidePanelOpen(true)}
                infoButtonRef={sidePanelReturnFocusRef}
                rootRef={setStickyHeaderNode}
              />
              <div
                ref={viewerGridRef}
                className={clsx(
                  styles.viewerGrid,
                  optionConfig.useViewportQueries ? styles.viewerGrid_viewport : styles.viewerGrid_container,
                )}
              >
                <div className={styles.viewerTitle}>
                  <Header
                    // RELAY-CLEANUP(IssueViewerIssue): open enum `state`/`stateReason` from still-Relay parent.
                    // Remove when IssueViewerIssue leaves Relay.
                    issue={issue as HeaderFragment}
                    linkedSecurityAlertsData={secondaryIssueData}
                    trackedBySecondaryData={secondaryIssueData}
                    subIssueSecondaryData={secondaryIssueData}
                    optionConfig={effectiveOptionConfig}
                    hideJumpToBottom={commentComposerAtTop}
                  />
                  <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
                </div>
                <div className={styles.viewerContent}>{issueContentArea}</div>
                <div
                  className={clsx(
                    styles.viewerSidePanel,
                    optionConfig.useViewportQueries
                      ? styles.viewerSidePanel_viewport
                      : styles.viewerSidePanel_container,
                  )}
                >
                  {!sidePanelOpen && (
                    <StickySidebar
                      topOffset={STICKY_HEADER_SIDEBAR_OFFSET}
                      recalculateOffsetOnResize={stickyHeaderEnabled}
                    >
                      {metadataPane}
                    </StickySidebar>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <Header
                // RELAY-CLEANUP(IssueViewerIssue): open enum `state`/`stateReason` from still-Relay parent.
                // Remove when IssueViewerIssue leaves Relay.
                issue={issue as HeaderFragment}
                linkedSecurityAlertsData={secondaryIssueData}
                trackedBySecondaryData={secondaryIssueData}
                subIssueSecondaryData={secondaryIssueData}
                optionConfig={effectiveOptionConfig}
                hideJumpToBottom={commentComposerAtTop}
              />
              <div ref={sentinelRef} className={styles.stickyHeaderSentinel} aria-hidden="true" />
              <ContentWrapper>
                <div
                  className={clsx(
                    styles.contentAndSidebarWrapper,
                    optionConfig.useViewportQueries
                      ? styles.contentAndSidebarWrapper_viewport
                      : styles.contentAndSidebarWrapper_container,
                  )}
                >
                  {issueContentArea}
                  <div
                    data-testid={TEST_IDS.issueViewerMetadataContainer}
                    className={clsx(
                      styles.metadataSidebar,
                      optionConfig.useViewportQueries
                        ? styles.metadataSidebar_viewport
                        : styles.metadataSidebar_container,
                    )}
                  >
                    {metadataPane}
                  </div>
                </div>
              </ContentWrapper>
            </>
          )}
          {stickyHeaderEnabled &&
            sidePanelOpen &&
            (optionConfig.insideSidePanel ? (
              <SidePanelMetadataDrawer onClose={() => setSidePanelOpen(false)} returnFocusRef={sidePanelReturnFocusRef}>
                {metadataPane}
              </SidePanelMetadataDrawer>
            ) : (
              <Dialog
                title="Details"
                position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
                width="360px"
                returnFocusRef={sidePanelReturnFocusRef}
                onClose={() => setSidePanelOpen(false)}
              >
                {metadataPane}
              </Dialog>
            ))}
          {secondaryIssueData && createDialogOpen && activeIssueId === issue.id && (
            <Suspense fallback={null}>
              <SubIssuesCreateDialog
                open={createDialogOpen}
                setOpen={closeCreateDialog}
                issue={secondaryIssueData}
                dialogTitle={optionConfig.subIssueCreateDialogTitle}
                onCreateSuccess={({createMore}): void => {
                  if (!createMore) {
                    closeCreateDialog()
                  }
                }}
              />
            </Suspense>
          )}
        </TimelineConnectionProvider>
      </IssueHeaderDataContext>
    </SubIssuesDataContext>
  )
}

function IsssueViewerWithSecondary({
  owner,
  repo,
  number,
  issueViewerViewRef,
  optionConfig,
  ...rest
}: IssueViewerWithSecondaryProps) {
  const [
    secondaryIssueData,
    hideClosedSubIssuesEnabled,
    initialNestedSubIssuesData,
    subIssueDeletionConnection,
    secondaryDataUpdatedAt,
  ] = useSecondaryQuery({owner, repo, number})

  // Defensive check: ensure query ref variables match requested issue to prevent stale data during navigation
  const queryVariables = issueViewerViewRef.variables
  if (queryVariables.number !== number || queryVariables.owner !== owner || queryVariables.repo !== repo) {
    return null
  }

  return (
    <>
      <IssueViewerInternal
        owner={owner}
        repo={repo}
        number={number}
        issueViewerViewRef={issueViewerViewRef}
        optionConfig={optionConfig}
        secondaryIssueData={secondaryIssueData ?? undefined}
        initialNestedSubIssuesData={initialNestedSubIssuesData}
        initialNestedSubIssuesDataUpdatedAt={secondaryDataUpdatedAt}
        hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
        issueUpdatesChannel={secondaryIssueData?.updatesChannel}
        subIssueDeletionConnection={subIssueDeletionConnection}
        {...rest}
      />
    </>
  )
}
